import { TopNav } from './TopNav';
import { SideNav } from './SideNav';
import { MobileNav } from './MobileNav';
import { MobileSidebar } from './MobileSidebar';
import { useState, useEffect, ReactNode } from 'react';
import { HeroShapes } from '../icons';
import { useAuth } from '../../../lib/AuthContext';
import 'react-loading-skeleton/dist/skeleton.css'
import Skeleton from 'react-loading-skeleton';
import { ProfileCompletionCTA } from '../ProfileCompletionCTA';
import { checkUserProfileCompleted } from '../../../utils/profileUtils';
import { captureAppException } from '../../../utils/errorCaptureUtils';
import { DecommissionBanner } from './DecommissionNotice';

interface MainLayoutProps {
  children: ReactNode;
  allowIncompleteProfile?: boolean;
}

export const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  allowIncompleteProfile = false
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { user } = useAuth();
  const [isProfileComplete, setIsProfileComplete] = useState<boolean>(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(true);

  useEffect(() => {
    const performProfileCheck = async () => {
      setIsLoadingProfile(true);
      try {
        if (user?.id) {
          const isComplete = await checkUserProfileCompleted(user.id);
          setIsProfileComplete(isComplete);
        } else {
          setIsProfileComplete(false);
        }
      } catch (error) {
        captureAppException(error, { tags: { context: 'MainLayout', action: 'profileCompletionCheck' } });
        setIsProfileComplete(false);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    performProfileCheck();
  }, [user]);

  const shouldBlockForProfileCompletion = !isProfileComplete && !allowIncompleteProfile;

  const layoutContent = (
    <div className="relative flex flex-col px-6 pt-4 pb-20 max-md:px-5">
      <div className="fixed inset-0 bg-greyscale-200 -z-100" />
      <div className="absolute top-0 left-0 right-0 bg-gray-900 h-48 -z-50">
        <HeroShapes className="absolute left-0 h-48 w-auto text-gray-800" />
      </div>
      <TopNav onOpenSidebar={() => setIsSidebarOpen(true)} />
      <div className="mt-4">
        <DecommissionBanner />
      </div>
      <main className="flex gap-6 mt-6 max-md:flex-col">
        <SideNav className="hidden md:flex" />
        <section className="flex flex-col flex-1 max-md:w-full">
          {isLoadingProfile ? (
            <Skeleton count={5} height={40} className="mb-4" />
          ) : shouldBlockForProfileCompletion ? (
            <ProfileCompletionCTA />
          ) : (
            children
          )}
        </section>
      </main>
      <MobileSidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      <MobileNav />
    </div>
  );

  return layoutContent;
};